// eslint-disable-next-line no-unused-vars
import { LoadingOutlined } from "@ant-design/icons";
// eslint-disable-next-line no-unused-vars
import { Button, Empty, Modal, notification, Table } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { translate } from "../../locale";
import PatientDocumentServices from "../../services/PatientDocumentServices";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const PatientDocumentsTable = ({ reload, onDelete }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const { username } = useParams();

  const confirmDelete = async (documentId) => {
    setDeletingId(documentId);
    try {
      await PatientDocumentServices.deleteDocument(documentId);
      setData((prev) => prev.filter((doc) => doc.id !== documentId));
      if (onDelete) {
        onDelete(documentId);
      }
    } catch (error) {
      console.error('confirmDelete error: ', error)
      notification.error({
        message: translate("No se pudo eliminar el documento"),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDelete = (documentId) => {
    Modal.confirm({
      title: translate("¿Está seguro que desea eliminar este documento?"),
      okText: translate("Eliminar"),
      cancelText: translate("Cancelar"),
      okType: "danger",
      onOk: () => confirmDelete(documentId),
    });
  };

  const columns = [
    {
      title: translate("Fecha"),
      dataIndex: "register_date",
      key: "register_date",
      render: (_, record) => moment(record.register_date).format("DD/MM/YYYY"),
      sorter: (a, b) => {
        if (a.register_date < b.register_date) return -1;
        if (a.register_date > b.register_date) return 1;
        return 0;
      },
      defaultSortOrder: "descend",
    },
    {
      title: translate("Tag"),
      dataIndex: "tag",
      key: "tag",
      render: (_, record) => record.tag?.name || "",
      sorter: (a, b) => {
        const aName = a.tag?.name || "";
        const bName = b.tag?.name || "";
        if (aName < bName) return -1;
        if (aName > bName) return 1;
        return 0;
      },
    },
    {
      title: translate("Acciones"),
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          <Button
            type="link"
            size="small"
            danger
            loading={deletingId === record.id}
            data-testid={`patient-documents-btn-delete-${record.id}`}
            onClick={() => handleDelete(record.id)}
          >
            {translate("Eliminar")}
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (reload !== null) {
      loadData();
    }
  }, [reload]);

  const loadData = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await PatientDocumentServices.getDocuments(username);
      if (res.status >= 200 && res.status < 300) {
        const docs = Array.isArray(res.data) ? res.data : (res.data.documents || []);
        setData(docs);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoadError(true);
      setLoading(false);
    }
  };

  if (loadError) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12"
        data-testid="patient-documents-load-error"
      >
        <p className="text-red-500 mb-4">
          {translate("No se pudieron cargar los documentos")}
        </p>
        <Button
          type="primary"
          onClick={loadData}
          data-testid="patient-documents-btn-retry"
        >
          {translate("Reintentar")}
        </Button>
      </div>
    );
  }

  return (
    <Table
      data-testid="patient-documents-table"
      rowKey={(i) => i.id}
      dataSource={data}
      columns={columns}
      loading={
        loading
          ? { indicator: antIcon, className: "text-custom_blue" }
          : false
      }
      locale={{
        emptyText: <Empty description={translate("No hay datos")} />,
        sortTitle: translate("Ordenar"),
        triggerDesc: translate("Ordenar (descendente)"),
        triggerAsc: translate("Ordenar (ascendente)"),
        cancelSort: translate("Cancelar orden"),
      }}
    />
  );
};

export default PatientDocumentsTable;
